import { useEffect, useRef } from 'react'

import { useAppDispatch } from '@/utils/typeHelpers'

import { recoverReport } from '../compliance.actions'

/** Each attempt costs a full narrative generation, so a reader switching tabs on a failing
 *  endpoint must not spend one per switch. Try again stays available past this. */
const MAX_AUTOMATIC_ATTEMPTS = 1

/** A backgrounded tab is suspended on iOS, which drops the in-flight generation and leaves
 *  the reader on an error screen for a request nothing refused.
 *
 *  Registration is unconditional because `visibilitychange` can beat the dropped request's
 *  rejection on a resumed tab, so a gated listener would attach after the event it needed
 *  to catch. `recoverReport` decides whether the stored failure is eligible. */
export const useRecoverReportOnResume = (): void => {
  const dispatch = useAppDispatch()
  const attemptsRef = useRef(0)

  useEffect(() => {
    const recover = () => {
      if (attemptsRef.current >= MAX_AUTOMATIC_ATTEMPTS) {
        return
      }
      // Counted only when it asked, so an ordinary tab switch does not spend the attempt.
      void dispatch(recoverReport()).then((asked) => {
        if (asked) {
          attemptsRef.current += 1
        }
      })
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recover()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('online', recover)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('online', recover)
    }
  }, [dispatch])
}
